import { DataManager } from '@syncfusion/ej2-data';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { Component, OnInit } from '@angular/core';
import { GridModule, PageService, FilterService, ToolbarService, SortService, EditService, CommandClickEventArgs, CommandModel } from '@syncfusion/ej2-angular-grids';
import { GridBaseComponent } from 'src/app/base/shared/grid-base/grid-base.component';
import { TitleBarComponent } from 'src/app/base/shared/title-bar/title-bar.component';
import { SearchFieldComponent } from 'src/app/base/shared/search-field/search-field.component';
import { CheckBoxModule } from '@syncfusion/ej2-angular-buttons';
import { ToolbarModule } from '@syncfusion/ej2-angular-navigations';

import { AvpCustomAdaptor } from 'src/app/base/shared/syncfusion/avp-custom-adaptor';
import { SpinnerService } from 'src/app/base/spinner/spinner.service';
import { AssetCollectionsService } from './asset-collections.service';
import { FormBuilder, Validators } from '@angular/forms';
import { AssetCollectionEditFormType } from './asset-collection-edit-form-type';
import { AssetCollectionAssetDTO, AssetCollectionDTO } from 'src/app/base/models/models.swagger.generated';
import { DateTime } from 'luxon';
import { AssetCollectionsEditComponent } from './asset-collections-edit/asset-collections-edit.component';
import { Router } from '@angular/router';
import { assetCollectionPublicViewPageName } from 'src/app/core/routing/page-names';

@Component({
    selector: 'app-asset-collections',
    templateUrl: './asset-collections.component.html',
    styleUrl: './asset-collections.component.scss',
    imports: [TranslateModule, GridModule, CheckBoxModule, ToolbarModule, TitleBarComponent, SearchFieldComponent, AssetCollectionsEditComponent],
    providers: [PageService, FilterService, ToolbarService, SortService, EditService]
})
export class AssetCollectionsComponent extends GridBaseComponent<AssetCollectionEditFormType> implements OnInit {

  constructor(translateService: TranslateService,
    private assetCollectionsService: AssetCollectionsService,
    private spinnerService: SpinnerService,
    private fb: FormBuilder,
    private router: Router) {
    super(translateService, assetCollectionsService);

    this.searchOptions = { fields: ['name', 'createdBy'], operator: 'contains' };

    this.gridCommands = [{
      type: 'Edit',
      buttonOption:
      {
        cssClass: 'icon-btn',
        iconCss: 'icon-edit e-icons'
      }
    } as CommandModel, {
      type: 'None',
      title: this.translateService.instant('i18n.domain.assets.asset-collections.open-collection'),
      buttonOption:
      {
        cssClass: 'icon-btn',
        iconCss: 'icon-eye e-icons'
      }
    } as CommandModel, {
      type: 'Delete',
      buttonOption:
      {
        cssClass: 'icon-btn',
        iconCss: 'icon-trash-can e-icons'
      }
    } as CommandModel];
  }

  ngOnInit(): void {
    this.editDialogWidth = 1080;
    this.data = new DataManager({
      adaptor: new AvpCustomAdaptor(this.assetCollectionsService, this.spinnerService),
    });
    this.editDialogTitle = 'i18n.domain.assets.asset-collections.edit.edit-asset-collection';
    this.addDialogTitle = 'i18n.domain.assets.asset-collections.add-collection';
  }

  public commandClick(args: CommandClickEventArgs) {
    if (args.commandColumn?.type === 'None') {
      const url = this.router.serializeUrl(
        this.router.createUrlTree(['/' + assetCollectionPublicViewPageName], { queryParams: { token: (args.rowData as AssetCollectionDTO).token } })
      );
      const baseUrl = window.location.origin;
      window.open(`${baseUrl}${url}`, '_blank');

    }
    else if (args.commandColumn?.type === 'Delete') {

    }
  }

  protected override initializeEditForm(data: AssetCollectionDTO): void {
    this.gridEditForm = this.fb.group<AssetCollectionEditFormType>({
      id: this.fb.control<number>(data?.id ?? 0),
      name: this.fb.control<string>(data?.name, [Validators.required]),
      description: this.fb.control<string>(data?.description),
      token: this.fb.control<string>({ value: data?.token, disabled: true }),
      isShared: this.fb.control<boolean>(false),
      sharedUntilDate: this.fb.control<Date>({ value: data?.sharedUntilDate?.toJSDate(), disabled: !data?.isShared }),
      assets: this.fb.control<AssetCollectionAssetDTO[]>(data?.assets),
    });
    this.gridEditForm.controls.isShared.valueChanges.subscribe((value) => {
      if (value) {
        this.gridEditForm.controls.sharedUntilDate.enable();
      } else {
        this.gridEditForm.controls.sharedUntilDate.disable();
      }
      this.gridEditForm.controls.sharedUntilDate.updateValueAndValidity();
    });
  }

  private setFormValues(data: AssetCollectionDTO) {
    this.gridEditForm.controls.id.setValue(data.id);
    this.gridEditForm.controls.name.setValue(data.name);
    this.gridEditForm.controls.description.setValue(data.description);
    this.gridEditForm.controls.token.setValue(data.token);
    this.gridEditForm.controls.isShared.setValue(data.isShared);
    this.gridEditForm.controls.sharedUntilDate.setValue(data.sharedUntilDate ? data.sharedUntilDate.toJSDate() : null);
    this.gridEditForm.controls.assets.setValue(data.assets);
  }

  protected override getEditDialogTitleParams(rowData: any): object {
    return { param: rowData.name };
  }

  public override actionComplete(args) {
    if (args.requestType === 'beginEdit') {
      this.spinnerService.show();
      this.assetCollectionsService.getRecord((args.rowData as AssetCollectionDTO).id).subscribe(
        {
          next: (assetCollection) => {
            this.spinnerService.hide();
            this.setFormValues(assetCollection);
          },
          error: (e) => {
            this.spinnerService.hide();
          }
        });
    }
    super.actionComplete(args);
  }

  protected override getDataToSave(): AssetCollectionDTO {
    return new AssetCollectionDTO({
      id: this.gridEditForm.controls.id.value,
      name: this.gridEditForm.controls.name.value,
      description: this.gridEditForm.controls.description.value,
      token: this.gridEditForm.controls.token.value,
      isShared: this.gridEditForm.controls.isShared.value,
      sharedUntilDate: this.gridEditForm.controls.sharedUntilDate.value
        ? DateTime.fromJSDate(this.gridEditForm.controls.sharedUntilDate.value).toUTC()
        : null,
      assets: this.gridEditForm.controls.assets.value,
    });
  }

  public override onAdd() {
    this.initializeEditForm(null)
    this.grid.addRecord();
  }
}
